import { useCallback, useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import dayjs from "dayjs";
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridFilterModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import ExtendButton from "../buttons/ExtendButton";
import WithdrawButton from "../buttons/WithdrawButton";
import { useMediaQuery } from "@mui/material";
import SplitOwnershipButton from "../buttons/SplitButton";
import { formatEther } from "ethers";
import {
  OrderDirection,
  Ownership,
  Ownership_OrderBy,
} from "../../../../apis/__generated__/graphql";
import { OwnershipTypes } from "../../../../libraries/utils/constants";
import theme from "../../../../theme";
import StatusChip from "../../../shared/StatusChip";
import { useAccount } from "@gu-corp/gu-wallet-connector";
import { useQuery } from "@apollo/client";
import { GET_OWNERSHIPS } from "../../../../libraries/queries/ownerships";
import { useMetadata } from "../../../../providers/ContractProvider";

export interface OwnershipTableProps {}

export default function OwnershipTable() {
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const { symbol } = useMetadata();
  const [selectedRow, setSelectedRow] = useState<Ownership | undefined>(
    undefined
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortModel, setSortModel] = useState<GridSortModel>();

  const statusFilter = filterModel?.items.find(
    (item) => item.field === "status"
  )?.value;

  const { account } = useAccount();

  const { data, loading, refetch } = useQuery(GET_OWNERSHIPS, {
    variables: {
      first: paginationModel.pageSize,
      skip: paginationModel.pageSize * paginationModel.page,
      orderDirection:
        sortModel?.[0]?.sort === "asc"
          ? OrderDirection.Asc
          : OrderDirection.Desc,
      orderBy: sortModel?.[0]?.field as Ownership_OrderBy,

      where: {
        status_in: statusFilter
          ? [statusFilter as OwnershipTypes]
          : [
              OwnershipTypes.PROVISIONAL,
              OwnershipTypes.VESTING_LOCKUP,
              OwnershipTypes.OWNERSHIP_LOCKUP,
            ],
        owner: account?.toLowerCase(),
      },
    },
    fetchPolicy: "network-only",
  });

  const ownerships = data?.ownerships || [];

  const onPaginationModelChange = useCallback<
    NonNullable<DataGridProProps["onPaginationModelChange"]>
  >((model) => {
    setPaginationModel(model);
  }, []);

  const onFilterModelChange = useCallback<
    NonNullable<DataGridProProps["onFilterModelChange"]>
  >(
    (model) => {
      setFilterModel(model);
    },
    [setFilterModel]
  );

  const onSortModelChange = useCallback<
    NonNullable<DataGridProProps["onSortModelChange"]>
  >(
    (model) => {
      setSortModel(model);
    },
    [setSortModel]
  );

  const columns: GridColDef[] = [
    {
      field: "index",
      headerName: "Index",
      valueGetter: ({ value }) => {
        return `#${value}`;
      },
      width: 90,
      minWidth: 90,
      filterable: false,
    },
    {
      field: "owner",
      headerName: "Owner",
      flex: 0.4,
      minWidth: 90,
      filterable: false,
      renderCell: ({ value }) => {
        return isLargeScreen ? "You" : value;
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      renderCell: ({ value }) => {
        return `${value} ${symbol}`;
      },
      minWidth: 120,
      type: "number",
      valueGetter: ({ value }) => formatEther(value),
      flex: 0.15,
      filterable: false,
    },
    {
      field: "lockupDeadline",
      headerName: "Lockup Deadline",
      renderCell: ({ value }) => {
        return Number(value) == 0
          ? "NOT SET"
          : dayjs(value).format("MM/DD/YYYY, HH:mm A");
      },
      type: "date",
      valueGetter: ({ value }) => dayjs.unix(value).toDate(),
      minWidth: 150,
      filterable: false,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      type: "singleSelect",
      valueOptions: [
        { value: OwnershipTypes.PROVISIONAL, label: "PROVISIONAL" },
        { value: OwnershipTypes.OWNERSHIP_LOCKUP, label: "OWNERSHIP_LOCKUP" },
        { value: OwnershipTypes.VESTING_LOCKUP, label: "VESTING_LOCKUP" },
      ],
      renderCell: ({ value }) => {
        return <StatusChip status={value} />;
      },
      filterable: true,
    },
    {
      field: "actions",
      type: "actions",
      width: 150,
      getActions: ({ row }) => [
        <ExtendButton
          key="Extend"
          disabled={
            (row.status !== OwnershipTypes.VESTING_LOCKUP &&
              row.status !== OwnershipTypes.OWNERSHIP_LOCKUP) ||
            row.lockupDeadline == 0
          }
          onSelectedRow={() => setSelectedRow(row)}
          onUnselectedRow={() => setSelectedRow(undefined)}
          row={selectedRow}
          refetch={refetch}
        />,
        <WithdrawButton
          key="Withdraw"
          disabled={
            (row.status !== OwnershipTypes.VESTING_LOCKUP &&
              row.status !== OwnershipTypes.OWNERSHIP_LOCKUP) ||
            dayjs.unix(row.lockupDeadline).isAfter(dayjs()) ||
            row.lockupDeadline == 0
          }
          onSelectedRow={() => setSelectedRow(row)}
          onUnselectedRow={() => setSelectedRow(undefined)}
          row={selectedRow}
          refetch={refetch}
        />,
        <SplitOwnershipButton
          key="Split"
          disabled={false}
          onSelectedRow={() => setSelectedRow(row)}
          onUnselectedRow={() => setSelectedRow(undefined)}
          row={selectedRow}
          refetch={refetch}
        />,
      ],
    },
  ];

  useEffect(() => {
    setPaginationModel({
      pageSize: 10,
      page: 0,
    });
  }, [filterModel, sortModel]);

  return (
    <Paper>
      <DataGridPro
        sx={{
          ".MuiTablePagination-displayedRows": {
            display: "none",
          },
        }}
        pagination
        rows={ownerships}
        columns={columns}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        rowCount={
          ownerships.length === 10
            ? paginationModel.pageSize * (paginationModel.page + 1) + 1
            : paginationModel.pageSize * (paginationModel.page + 1)
        }
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        loading={loading}
        autoHeight
        paginationModel={paginationModel}
        sortModel={sortModel}
        filterModel={filterModel}
        onPaginationModelChange={onPaginationModelChange}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
      />
    </Paper>
  );
}
